import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type Cell,
  type CellData,
  type Column,
  type Header,
  type RowData,
  type Table,
  type TableFeature,
  type TableState,
} from '@tanstack/react-table';
import { isValidElement, type MouseEvent as ReactMouseEvent } from 'react';

import { isProduction } from '../../../../core/utils/is-node-environment.js';
import { devLog } from '../../../../core/utils/logging.js';
import { column_getCanSort } from '../Sorting/Sorting.js';
import { SortingColumnActions } from '../Sorting/SortingColumnActions.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';
import type {
  CollectedCellActions,
  DataTableCellActionsContent,
} from './CellActions.js';
import type { CollectedColumnActions } from './ColumnActions.js';
import {
  filterActionsForChartColumn,
  getColumnActionsElements,
} from './user-actions-utils.js';

export function table_getHeader<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  headerId: string,
  orderedHeaderGroups: Header<TDataTableFeatures, TData, unknown>[][],
): Header<TDataTableFeatures, TData> | undefined {
  // Group header ids carry the pinning family prefix (`start_`/`center_`/`end_`)
  // only in the ordered header groups, which is also what renders the header
  // cells and their `data-headerid`. Looking the id up anywhere else misses
  // every group header.
  const header = orderedHeaderGroups
    .flat()
    .find((header) => header.id === headerId);

  if (!isProduction() && !header) {
    devLog(`[Table] Header with id '${headerId}' does not exist.`);
  }
  return header;
}

/**
 * Helper function to set the floating menu info in the table state.
 * @internal
 */
export function table_setFloatingMenuInfo<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  headerId: string | null,
  columnId: string,
  rowId: string | null,
  x: number,
  y: number,
): void {
  table.baseAtoms.floatingMenuInfo.set({
    type: rowId === null ? 'headerAction' : 'cellAction',
    headerId,
    rowId,
    columnId,
    x,
    y,
  });
  table_setOpenMenuRowId(table, rowId);
}

/**
 * Helper function to set the row id for the open menu.
 * @internal
 */
export function table_setOpenMenuRowId<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  rowId: string | null,
): void {
  table.baseAtoms.openMenuRowId.set(rowId);
}

/**
 * Helper function to get the row id for the open menu.
 * @internal
 */
export function table_getOpenMenuRowId<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
): string | null {
  return table.atoms.openMenuRowId.get();
}

/**
 * Helper function to get the column actions for a given columnId.
 * @internal
 */
export function table_getUserActionsForColumn<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  columnId: string,
) {
  const column = table.getColumn(columnId);

  if (column?.getCanColumnActions()) {
    return column.getColumnActions();
  }

  return null;
}

/**
 * Helper function to get the cell actions for a given columnId and rowId.
 * @internal
 */
export function table_getUserActionsForCell<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  columnId: string,
  rowId: string,
) {
  const row = table.getRow(rowId);
  const cell = row
    .getVisibleCells()
    .find((cell) => cell.column.id === columnId);

  if (cell?.column.getCanCellActions()) {
    return cell.getCellActions();
  }

  return null;
}

/**
 * Helper function to get the floating menu context menu handler for the user actions feature.
 * @internal
 */
export function table_getUserActionFloatingMenuContextMenuHandler<
  TData extends RowData,
>(
  table: Table<TDataTableFeatures, TData>,
): (event: ReactMouseEvent<HTMLDivElement>) => void {
  return (event: ReactMouseEvent<HTMLDivElement>) => {
    if (
      event.target instanceof HTMLElement === false &&
      event.target instanceof SVGElement === false
    ) {
      return;
    }

    const cellElement = event.target.closest<HTMLElement>('[role="cell"]');
    // the click was on a body cell
    if (cellElement) {
      const { columnid } = cellElement.dataset;
      const rowid = cellElement
        .closest('[role="row"]')
        ?.getAttribute('data-rowid');

      if (!columnid || !rowid) {
        return;
      }

      // In order to check if the column or cell could take actions we get the actual cell objects.
      const row = table.getRow(rowid);
      const cell = row.getAllCellsByColumnId()[columnid];

      if (cell?.column.getCanCellActions()) {
        event.preventDefault();
        event.stopPropagation();
        table_setFloatingMenuInfo(
          table,
          null, // header is not needed for floating menu on body cell
          columnid,
          rowid,
          event.pageX,
          event.pageY,
        );
      }
      return;
    }

    const headerElement = event.target.closest<HTMLElement>(
      '[role="columnheader"]',
    );
    // the click happened on a header cell
    if (headerElement) {
      const { columnid, headerid } = headerElement.dataset;
      if (!columnid) {
        return;
      }

      // In order to check if the column or cell could take actions we get the actual column object.
      const column = table.getColumn(columnid);

      if (column?.getCanColumnActions()) {
        event.preventDefault();
        event.stopPropagation();
        table_setFloatingMenuInfo(
          table,
          headerid ?? null,
          columnid,
          null,
          event.pageX,
          event.pageY,
        );
      }
    }
  };
}

/**
 * Helper function to get the column actions for a given column.
 * @internal
 */
export function column_getColumnActions<TData extends RowData>(
  column: Column<TDataTableFeatures, TData>,
  columnId: string,
  columnActionsOption?: CollectedColumnActions<TData> | undefined,
  enableSortingOption?: boolean,
) {
  const columnActions = getColumnActionsElements(
    columnId,
    columnActionsOption ?? [],
    column.table.options.consumerColumnDefMap,
  );

  const isChartColumn =
    typeof column.columnDef.enableUserActions === 'object' &&
    column.columnDef.enableUserActions.type === 'chart';
  const filteredActions = isChartColumn
    ? filterActionsForChartColumn(columnActions)
    : columnActions;

  // Append sorting actions at the end when sorting is enabled for this column.
  if (enableSortingOption && column_getCanSort(column)) {
    filteredActions.push(
      <SortingColumnActions
        key="strato-table-sorting-column-actions"
        hasOtherColumnActions={filteredActions.length > 0}
      />,
    );
  }

  return filteredActions;
}

/**
 * Helper function to get if a column has any column actions.
 * @internal
 */
export function column_getCanColumnActions<TData extends RowData>(
  column: Column<TDataTableFeatures, TData>,
) {
  if (column.columnDef.enableUserActions === false) {
    return false;
  }

  const columnActionsEnabled = column.table.options.enableColumnActions;
  const sortingEnabled =
    column.table.options.enableSorting && column_getCanSort(column);

  // the column cannot have any actions if neither column actions nor sorting are enabled for this column.
  if (!columnActionsEnabled && !sortingEnabled) {
    return false;
  }

  const definedColumnActions = column.getColumnActions();
  if (!definedColumnActions) {
    // runtime safety net
    return false;
  }

  return (definedColumnActions.length ?? 0) > 0;
}

/**
 * Helper function to get the cell actions for a given columnId.
 * @internal
 */
export function column_getCellActionsContent<TData extends RowData>(
  column: Column<TDataTableFeatures, TData>,
  columnId: string,
  cellActionsOptions: CollectedCellActions<TData> = [],
): Array<DataTableCellActionsContent<TData>> {
  return cellActionsOptions.reduce(
    (actions, cellAction) => {
      if (
        cellAction.column === undefined ||
        // global action for all columns
        cellAction.column === columnId ||
        // column-specific action
        (typeof cellAction.column === 'function' && cellAction.column(columnId))
      ) {
        actions.push(cellAction.action);
      }
      return actions;
    },
    [] as Array<DataTableCellActionsContent<TData>>,
  );
}

/**
 * Helper function to get if the column has any cell actions.
 * @internal
 */
export function column_getCanCellActions<TData extends RowData>(
  column: Column<TDataTableFeatures, TData>,
): boolean {
  if (column.columnDef.enableUserActions === false) {
    return false;
  }

  if (!column.table.options.enableCellActions) {
    return false;
  }

  const definedCellActions = column.getCellActionsContent();
  if (!definedCellActions) {
    // runtime safety net
    return false;
  }

  return (definedCellActions.length ?? 0) > 0;
}

/**
 * Helper function to get the cell actions for a given cell.
 * @internal
 */
export function cell_getCellActions<
  TData extends RowData,
  TValue extends CellData,
>(cell: Cell<TDataTableFeatures, TData, TValue>) {
  const cellActions = cell.column.getCellActionsContent().map((cellAction) => {
    // If the entry is a react element, we can directly return it
    if (isValidElement(cellAction)) {
      return cellAction;
    }
    // If it is a function, we will need to resolve the closure first
    if (typeof cellAction === 'function') {
      const columnDef = cell.table.options.consumerColumnDefMap?.get(
        cell.column.id,
      );
      if (columnDef !== undefined) {
        return cellAction({
          columnDef: columnDef,
          cellValue: cell.getValue(),
          row: cell.row.original,
          rowId: cell.row.id,
        });
      }
    }
    return null;
  });

  return cellActions;
}

/**
 * Constructs the user actions feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructUserActionsFeature<
  TData extends RowData,
>(): TableFeature {
  return {
    getInitialState: (initialState) => {
      return {
        ...initialState,
        floatingMenuInfo: structuredClone({
          columnId: null,
          rowId: null,
          x: 0,
          y: 0,
        }),
        openMenuRowId: null,
      } as unknown as TableState<TDataTableFeatures>;
      // This is safe here as the DataTableFeatures configuration, so it is a valid part of
      // TableState<TDataTableFeatures> at runtime — TypeScript just can't prove it
      // through the UnionToIntersection generic constraint.
    },
    assignColumnPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('DataTableUserActionsFeature', prototype, table, {
          column_getColumnActions: {
            fn: (
              column,
              columnId: string,
              columnActionsOption?: CollectedColumnActions<TData> | undefined,
              enableSorting?: boolean,
            ) =>
              column_getColumnActions(
                column,
                columnId,
                columnActionsOption,
                enableSorting,
              ),
            memoDeps: (column) => [
              column.id,
              table.options.columnActions,
              table.options.enableSorting,
            ],
          },
          column_getCanColumnActions: {
            fn: (column) => column_getCanColumnActions(column),
          },
          column_getCellActionsContent: {
            fn: (
              column,
              columnId: string,
              cellActionsOptions: CollectedCellActions<TData>,
            ) =>
              column_getCellActionsContent(
                column,
                columnId,
                cellActionsOptions,
              ),
            memoDeps: (column) => [column.id, table.options.cellActions],
          },
          column_getCanCellActions: {
            fn: (column) => column_getCanCellActions(column),
          },
        });
      },
    ),
    assignCellPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('DataTableUserActionsFeature', prototype, table, {
          cell_getCellActions: {
            fn: (cell) => cell_getCellActions(cell),
            memoDeps: (cell) => [cell.column.id, table.options.cellActions],
          },
        });
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('DataTableUserActionsFeature', table, {
          table_getHeader: {
            fn: (
              headerId: string,
              orderedHeaderGroups: Header<
                TDataTableFeatures,
                TData,
                unknown
              >[][],
            ) =>
              table_getHeader<TData>(
                table as unknown as Table<TDataTableFeatures, TData>,
                headerId,
                orderedHeaderGroups,
              ),
            memoDeps: (headerId) => [headerId, table.getOrderedHeaderGroups()],
          },
          table_setFloatingMenuInfo: {
            fn: (
              headerId: string | null,
              columnId: string,
              rowId: string | null,
              x: number,
              y: number,
            ) =>
              table_setFloatingMenuInfo(table, headerId, columnId, rowId, x, y),
          },
          table_setOpenMenuRowId: {
            fn: (rowId: string | null) => table_setOpenMenuRowId(table, rowId),
          },
          table_getOpenMenuRowId: {
            fn: () => table_getOpenMenuRowId(table),
          },
          table_getUserActionsForColumn: {
            fn: (columnId: string) =>
              table_getUserActionsForColumn(table, columnId),
          },
          table_getUserActionsForCell: {
            fn: (columnId: string, rowId: string) =>
              table_getUserActionsForCell(table, columnId, rowId),
          },
          table_getUserActionFloatingMenuContextMenuHandler: {
            fn: () => table_getUserActionFloatingMenuContextMenuHandler(table),
          },
        });
      },
    ),
  };
}

/**
 * Feature to handle user actions such as column and cell actions, including floating menu context.
 * @internal
 */
export const DataTableUserActionsFeature = constructUserActionsFeature();
